import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormButton } from '@app/core/components/form-field-button/form-field-button.component';
import { Item } from '@app/core/components/listbox/listbox.component';
import { DataTaskService } from '@app/core/services/data-task.service';
import { FileItem } from '@app/core/services/file.service';
import { LayoutService } from '@app/core/services/layout.service';
import { ModalService } from '@app/core/services/modal.service';
import { UtilityService } from '@app/core/services/utility.service';
import { AccountingService, ExchangeRate, LabourerItem, VoucherNoItem, VoucherTypeItem, VoucherTypeLayoutData } from '@app/services/accounting.service';
import { CustomerEditGet } from '@app/services/customer.service';
import { DocumentItem } from '@app/services/document.service';
import { EconomyService, ValutaItem } from '@app/services/economy.service';
import { BusinesscoItem, DepartmentItem, LogisticService } from '@app/services/logistic.service';
import { CustomerAccountInfo, SearchProjectItem, TypeaheadService } from '@app/services/typeahead.service';

@Component({
    selector: 'tt-accounting-processing-head',
    templateUrl: './accounting-processing-head.component.html',
    styleUrls: ['./accounting-processing-head.component.css'],
})
export class AccountingProcessingHeadComponent implements OnInit, OnChanges {
    @Input()
    public accountingfunction?: string;

    @Input()
    public head?: AccountingProcessingHead;

    @Output()
    public headChange = new EventEmitter<AccountingProcessingHead>();

    @Output()
    public change = new EventEmitter<AccountingProcessingHeadChangeEvent>();

    public openNewCustomerModal?: () => unknown = async () => {
        if (!this.head) return;

        const submitted: (CustomerEditGet & { addContactsAfterNewCustomer: boolean }) | undefined = await this.modal.openStateDialog({ state: 'customeredit', params: '0' });

        if (submitted) {
            this.head.cust_no = '' + submitted.cust_no;
            this.head.acc_supp_no = submitted.acc_supp_no;
            this.head.supplier_name = submitted.cust_name;
            this.head.bankaccount = submitted.bankaccount;
            this.head.bic = submitted.bic;
            this.head.iban = submitted.iban;

            this.saveCustact('cust_no', 'acc_supp_no', 'supplier_name', 'bankaccount', 'bic', 'iban');
        }
    };

    @Output()
    public voucherTypeChanged = new EventEmitter<string>();

    public voucherTypes: VoucherTypeItem[] = [];

    public valutas: ValutaItem[] = [];

    public departments: DepartmentItem[] = [];

    public labourers: LabourerItem[] = [];

    public businessCos: BusinesscoItem[] = [];

    public supplierButtons: FormButton[] = [{ id: 'goto', icon: 'fas fa-chevron-right', type: 'primary', goto: 'customersmenu', gotoParms: () => this.head?.cust_no ?? '' }];

    public supplierEmptyButtons: FormButton[] = [{ id: 'new', icon: 'fas fa-plus', type: 'primary', onClick: () => this.openNewCustomerModal?.() }];

    public equipmentButtons: FormButton[] = [{ id: 'list', icon: 'fas fa-bars', type: 'primary', goto: 'equipments' }];

    constructor(
        public layout: LayoutService,
        private datatask: DataTaskService,
        private accounting: AccountingService,
        private economy: EconomyService,
        private logistic: LogisticService,
        private typeahead: TypeaheadService,
        private utility: UtilityService,
        private modal: ModalService,
    ) {
        //
    }

    public searchCustomerAccountInfo = () => {
        return this.typeahead.searchCustomerAccountInfo({ cust_name: this.head?.cust_name ?? '', custtype: 'C', lastselect: 'SEARCH' });
    };

    public searchSupplierAccountInfo = async () => {
        return this.typeahead.searchCustomerAccountInfo({ cust_name: this.head?.supplier_name ?? '', custtype: 'S', lastselect: 'SEARCH' });
    };

    public searchProjects = async () => {
        return this.typeahead.searchProjects({ project_name: this.head?.project_name ?? '', businessco_no: this.head?.businessco_no ?? '' });
    };

    public searchVoucherNoMatch = async () => {
        return this.accounting.searchVoucherNoMatch(this.head!);
    };

    public searchEquipments = async () => {
        return this.typeahead.searchEquipments(this.head?.equipment_name ?? '');
    };

    public async onVoucherTypeChanged() {
        const voucherTypeData = await this.getVoucherTypeLayoutData();

        if (!!this.head && voucherTypeData) {
            this.head = { ...this.head, ...voucherTypeData };

            this.saveCustact('acvouchertype_id', ...(Object.keys(voucherTypeData) as (keyof VoucherTypeLayoutData)[]));
            this.voucherTypeChanged.emit(this.head.acvouchertype_id);
        }
    }

    public onCustomerSelected(item?: CustomerAccountInfo) {
        if (!!this.head) {
            this.head.cust_name = item?.item_name ?? '';
            this.head.cust_no = item?.item_id ?? '0';
            this.head.acc_cust_no = item?.acc_cust_no ?? '0';

            this.custactColChanged('cust_no');
        }
    }

    public onVoucherNoMatchSelected(item?: VoucherNoItem) {
        if (!!this.head) {
            this.head.voucher_no_match = item?.voucher_no ?? '';

            this.custactColChanged('voucher_no_match');
        }
    }

    public onSupplierSelected(item?: CustomerAccountInfo) {
        if (!!this.head) {
            this.head.supplier_name = item?.item_name ?? '';
            this.head.cust_no = item?.item_id ?? '0';
            this.head.acc_supp_no = item?.acc_supp_no ?? '0';

            if (!!item) {
                this.head.bankaccount = item.bankaccount;
                this.head.bic = item.bic;
                this.head.iban = item.iban;
            }

            this.custactColChanged('cust_no');
        }
    }

    public onAmountInvvalAbsChanged() {
        if (!!this.head && !isNaN(parseFloat('' + this.head.amount_invval_abs))) {
            this.recalculateAmountLocValAbs();
            this.saveCustact('amount_locval_abs');
        }
    }

    public async onValutaChanged() {
        if (!!this.head && this.head.base_currency_id > '0' && this.head.valuta_id && this.head.valuta_id > '0') {
            this.head = { ...this.head, ...(await this.getExchangeRate()) };

            this.recalculateAmountLocValAbs();
            this.saveCustact('amount_locval_abs');
        }
    }

    public onExchangeRateChanged() {
        if (!!this.head && !isNaN(parseFloat('' + this.head.exchange_rate))) {
            this.recalculateExchangeFactor();
            this.recalculateAmountLocValAbs();
            this.saveCustact('exchange_factor', 'amount_locval_abs');
        }
    }

    public onProjectSelected(item?: SearchProjectItem) {
        if (!!this.head) {
            this.head.project_name = item?.item_name ?? '';
            this.head.project_keyno = item?.item_id ?? '';
            this.saveCustact('project_name', 'project_keyno');
        }
    }

    public onEquipmentSelected(item?: Item) {
        if (!!this.head) {
            this.head.equipment_id = item?.item_id ?? '0';
            this.head.equipment_name = item?.item_name ?? '';
            this.saveCustact('equipment_name', 'equipment_id');
        }
    }

    public saveCustact(...fields: (keyof AccountingProcessingHead)[]) {
        if (!!this.head) {
            this.headChange.emit(this.head);
            this.change.emit({ type: 'accountingprocessingheadchange', data: this.head, fields: fields });
        }
    }

    public async custactColChanged(key: keyof AccountingProcessingHead) {
        const newData = await this.getCustactColChanged({ ...(this.head as AccountingProcessingHead), colname_changed: key });

        this.head = { ...this.head, ...newData } as AccountingProcessingHead;

        this.saveCustact(key, ...(Object.keys(newData) as (keyof AccountingProcessingHead)[]));
    }

    private recalculateAmountLocValAbs() {
        if (!!this.head) {
            this.head.amount_locval_abs = this.utility.parseNumber(this.head.amount_invval_abs) * this.utility.parseNumber(this.head.exchange_factor);
        }
    }

    private recalculateExchangeFactor() {
        if (!!this.head) {
            this.head.exchange_factor = (this.utility.parseNumber(this.head.exchange_rate) / this.utility.parseNumber(this.head.valuta_pr)).toFixed(6);
        }
    }

    private async loadVoucherTypes() {
        this.voucherTypes = await this.accounting.getVoucherTypes();
    }

    private async loadValutas() {
        this.valutas = await this.economy.getValutas();
    }

    private async loadDepartments() {
        if (!!this.head && this.head.businessco_no > '0') {
            this.departments = await this.logistic.getDepartments(this.head.businessco_no);
        }
    }

    private async loadLabourers() {
        if (!!this.head && this.head.businessco_no > '0') {
            this.labourers = await this.accounting.getLabourers({ businessco_no: this.head.businessco_no });
        }
    }

    private async loadBusinessCos() {
        this.businessCos = await this.logistic.getCompanies();
    }

    private async getVoucherTypeLayoutData(): Promise<VoucherTypeLayoutData | null> {
        if (!!this.head && this.head.acvouchertype_id > '0') {
            try {
                return await this.accounting.getVoucherTypeLayoutData({ acvouchertype_id: this.head.acvouchertype_id, accountingfunction: this.accountingfunction ?? '' });
            } catch {}
        }

        return null;
    }

    private async getExchangeRate(): Promise<ExchangeRate | null> {
        if (!!this.head) {
            try {
                return await this.accounting.getExchangeRate(this.head.base_currency_id, this.head.valuta_id);
            } catch {}
        }

        return null;
    }

    private async getCustactColChanged(data: AccountingProcessingHead & { colname_changed: keyof AccountingProcessingHead }): Promise<Partial<AccountingProcessingHead>> {
        try {
            return (await this.datatask.Post(3186, data))[0];
        } catch {
            return {};
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!!changes?.['head']) {
            this.loadDepartments();
            this.loadLabourers();
        }
    }

    ngOnInit(): void {
        this.loadVoucherTypes();
        this.loadValutas();
        this.loadBusinessCos();
    }
}

export interface AccountingProcessingHead extends VoucherTypeLayoutData, ExchangeRate {
    acc_cust_no: string;
    acc_supp_no: string;
    acvoucherbunt_keyno: string;
    acvouchertype_id: string;
    amount_invval_abs: string | number;
    amount_locval_abs: string | number;
    bankaccount: string;
    base_currency_id: string;
    bic: string;
    businessco_no: string;
    custactac_keyno: string;
    custact_no: string;
    cust_no: string;
    cust_name: string;
    department_id: string;
    docarc_keyno: string;
    duedate: string;
    equipment_id: string | number;
    equipment_name: string;
    iban: string;
    kid: string;
    labour_no: string;
    nbr_of_custactdialogs: string;
    note: string;
    project_keyno: string | number;
    project_name: string;
    rem_invoice_no: string | number;
    rem_on_hold: '1' | '0';
    rem_never_pay: '1' | '0';
    supplier_name: string;
    transdate: string;
    validationmessage: string;
    valuta_id: string;
    voucherdate: string;
    voucher_no_match: string;
    show_comments_preview: '1' | '0';
    show_office_signin: '1' | '0';
    custactDocument?: DocumentItem & FileItem;
}

export interface AccountingProcessingHeadChangeEvent {
    type: 'accountingprocessingheadchange';
    data: AccountingProcessingHead;
    fields: (keyof AccountingProcessingHead)[];
}
